import { skipStringOrComment } from '@/common/utils/evals/script-async-analyzer'

interface LiteralRegion {
  start: number
  end: number
}

function buildLiteralRegions(script: string): LiteralRegion[] {
  const regions: LiteralRegion[] = []
  const len = script.length
  let i = 0

  while (i < len) {
    const skipped = skipStringOrComment(script, i, len)
    if (skipped === -1) {
      i++
      continue
    }
    regions.push({ start: i, end: skipped })
    i = skipped
  }

  return regions
}

function isInsideRegion(index: number, regions: LiteralRegion[]): boolean {
  return regions.some((region) => index >= region.start && index < region.end)
}

function skipOutOfRegion(index: number, regions: LiteralRegion[]): number {
  let i = index
  while (i >= 0 && isInsideRegion(i, regions)) {
    const region = regions.find((entry) => i >= entry.start && i < entry.end)
    if (!region) break
    i = region.start - 1
  }
  return i
}

function isWhitespace(ch: string): boolean {
  return ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r'
}

function skipWhitespaceBack(script: string, index: number): number {
  let i = index
  while (i >= 0 && isWhitespace(script[i])) i--
  return i
}

function isIdentPart(ch: string): boolean {
  return /[A-Za-z0-9_$]/.test(ch)
}

function findMatchingParenBack(script: string, closePos: number, regions: LiteralRegion[]): number {
  let depth = 1
  let i = closePos - 1

  while (i >= 0) {
    i = skipOutOfRegion(i, regions)
    if (i < 0) return -1

    const ch = script[i]
    if (ch === ')') depth++
    else if (ch === '(') {
      depth--
      if (depth === 0) return i
    }
    i--
  }

  return -1
}

function findMatchingBracketBack(script: string, closePos: number, regions: LiteralRegion[]): number {
  let depth = 1
  let i = closePos - 1

  while (i >= 0) {
    i = skipOutOfRegion(i, regions)
    if (i < 0) return -1

    const ch = script[i]
    if (ch === ']') depth++
    else if (ch === '[') {
      depth--
      if (depth === 0) return i
    }
    i--
  }

  return -1
}

function parseStringLiteralStart(script: string, endQuotePos: number): number {
  const quote = script[endQuotePos]
  let i = endQuotePos - 1
  while (i >= 0) {
    if (script[i] === quote) {
      if (i > 0 && script[i - 1] === '\\') {
        i -= 2
        continue
      }
      return i
    }
    i--
  }
  return -1
}

function parsePrimaryStart(script: string, end: number, regions: LiteralRegion[]): number {
  let i = skipWhitespaceBack(script, end)
  if (i < 0) return -1

  const ch = script[i]

  if (ch === ')') {
    return findMatchingParenBack(script, i, regions)
  }

  if (ch === ']') {
    return findMatchingBracketBack(script, i, regions)
  }

  if (ch === '"' || ch === "'" || ch === '`') {
    return parseStringLiteralStart(script, i)
  }

  if (isIdentPart(ch)) {
    while (i >= 0 && isIdentPart(script[i])) i--
    return i + 1
  }

  if (ch >= '0' && ch <= '9') {
    while (i >= 0 && /[0-9.]/.test(script[i])) i--
    return i + 1
  }

  return -1
}

function maybeExtendCallExpression(script: string, start: number): number {
  if (script[start] !== '(') return start
  let i = skipWhitespaceBack(script, start - 1)
  if (i < 0 || !isIdentPart(script[i])) return start
  while (i >= 0 && isIdentPart(script[i])) i--
  return i + 1
}

function parseExpressionStart(script: string, endExclusive: number, regions: LiteralRegion[]): number {
  let end = skipWhitespaceBack(script, endExclusive - 1)
  if (end < 0) return -1

  let start = parsePrimaryStart(script, end, regions)
  if (start < 0) return -1
  start = maybeExtendCallExpression(script, start)

  while (true) {
    let i = skipWhitespaceBack(script, start - 1)
    if (i < 0) break

    if (script[i] === '.') {
      i = skipWhitespaceBack(script, i - 1)
      if (i < 0 || !isIdentPart(script[i])) return -1
      while (i >= 0 && isIdentPart(script[i])) i--
      start = i + 1
      continue
    }

    if (script[i] === ']') {
      const bracketStart = findMatchingBracketBack(script, i, regions)
      if (bracketStart < 0) return -1
      start = bracketStart
      continue
    }

    break
  }

  return start
}

function findLengthCallEnd(script: string, openParenPos: number, regions: LiteralRegion[]): number {
  let depth = 1
  let i = openParenPos + 1
  const len = script.length

  while (i < len && depth > 0) {
    if (!isInsideRegion(i, regions)) {
      const skipped = skipStringOrComment(script, i, len)
      if (skipped !== -1) {
        i = skipped
        continue
      }
      if (script[i] === '(') depth++
      else if (script[i] === ')') depth--
    }
    i++
  }

  return depth === 0 ? i : -1
}

function findNextJavaLengthReplacement(
  script: string,
): { start: number; end: number; receiver: string } | null {
  const regions = buildLiteralRegions(script)
  const len = script.length
  let i = 0

  while (i < len) {
    if (isInsideRegion(i, regions)) {
      i++
      continue
    }

    const skipped = skipStringOrComment(script, i, len)
    if (skipped !== -1) {
      i = skipped
      continue
    }

    if (script.startsWith('.length', i)) {
      let cursor = i + '.length'.length
      while (cursor < len && isWhitespace(script[cursor])) cursor++

      if (script[cursor] === '(') {
        const callEnd = findLengthCallEnd(script, cursor, regions)
        if (callEnd >= 0) {
          let argsEnd = cursor + 1
          while (argsEnd < callEnd - 1 && isWhitespace(script[argsEnd])) argsEnd++
          if (argsEnd === callEnd - 1) {
            const receiverStart = parseExpressionStart(script, i, regions)
            if (receiverStart >= 0) {
              return {
                start: receiverStart,
                end: callEnd,
                receiver: script.slice(receiverStart, i),
              }
            }
          }
        }
      }
    }

    i++
  }

  return null
}

/**
 * Заменяет Java/Rhino-вызовы `.length()` на `javaLength(expr)` для eval в En.run.
 */
export function transformJavaLengthCalls(script: string): string {
  let result = script
  while (true) {
    const replacement = findNextJavaLengthReplacement(result)
    if (!replacement) return result
    result =
      `${result.slice(0, replacement.start)}javaLength(${replacement.receiver})${result.slice(replacement.end)}`
  }
}
